import { useEffect, useCallback } from "react";
import Win2kButton from "@/components/win2k/Win2kButton";

interface RulesModalProps {
  open: boolean;
  onClose: () => void;
}

export default function RulesModal({ open, onClose }: RulesModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="win2k-outset bg-[var(--win2k-window-bg)] max-w-sm w-full mx-4 max-h-[85vh] flex flex-col">
        <div className="win2k-title-bar px-1 py-0.5 min-h-[22px] flex items-center justify-between">
          <span className="text-xs font-bold">How to Play</span>
          <button
            type="button"
            onClick={onClose}
            className="win2k-outset bg-[var(--win2k-button-face)] w-[16px] h-[14px] flex items-center justify-center text-[10px] leading-none font-bold text-black"
          >
            ×
          </button>
        </div>

        <div className="p-3 space-y-3 overflow-y-auto text-[11px] text-[var(--win2k-button-text)] leading-relaxed">
          <p className="font-bold text-[12px] text-[var(--win2k-black)]">
            Draw cards. Don't bust. First to 200 wins!
          </p>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">The Basics</p>
            <p>
              Each turn you <strong>Hit</strong> (draw a card) or <strong>Stay</strong> (keep your score).
              Your score = the sum of your number cards.
            </p>
          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">Busting</p>
            <p>
              Draw a <strong>duplicate number</strong> you already have? You're bust — <strong>0 points</strong> for the round. Ouch.
            </p>
          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">The Deck</p>
            <p className="mt-1">
              The deck has numbers <strong>0–12</strong>, and here's the catch: each number appears as many times as its value. So there's only one 1, but six 6s and twelve 12s. Higher cards are easier to draw — but also easier to bust on!
            </p>

          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">Special Cards</p>
            <ul className="list-none space-y-1 mt-1">
              <li><strong>Freeze</strong> — Force another player to stay with their current cards.</li>
              <li><strong>Second Chance</strong> — Give a player a safety net. If they draw a duplicate, it's discarded instead of busting them. One use.</li>
              <li><strong>Flip Three</strong> — Force another player to draw 3 cards in a row. Risky!</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">Bonus Cards</p>
            <ul className="list-none space-y-1 mt-1">
              <li><strong>+2, +4, +6, +8, +10</strong> — Added to your score.</li>
              <li><strong>×2</strong> — Doubles the sum of your numbers before bonuses are added.</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">Flip 7 🎉</p>
            <p>
              Collect <strong>7 different numbers</strong> and you get a <strong>+15 bonus</strong> — plus the round ends immediately. Go big or go home!
            </p>
          </div>

          <div>
            <p className="font-bold text-[var(--win2k-black)]">Winning</p>
            <p>
              Rounds repeat until someone hits <strong>200 points</strong>. Highest total score wins!
            </p>
          </div>
        </div>

        <div className="p-2 flex justify-center border-t border-[var(--win2k-shadow)]">
          <Win2kButton onClick={onClose} className="min-w-[75px]" autoFocus>
            Got it!
          </Win2kButton>
        </div>
      </div>
    </div>
  );
}
